/**
 * สร้างคิวของ session
 *
 * 2 แบบคนละหลักการ:
 *   SRS   — bushu/kanji/vocab · ครบกำหนดแล้วค่อยเจอ
 *   Drill — kana · 46 ตัวจำเสียงเดียว จบใน 2-3 วัน SRS overkill (NOTES.md §11)
 */
import type { Card, CardId, DrillStat, Progress } from './types'
import { CardState } from './types'

export interface QueueOptions {
  /** จำกัดจำนวนใบใน session — โหมด 2 นาทีตั้ง 20 */
  limit?: number
  now?: number
}

/**
 * คิวทวน — เอาเฉพาะใบที่ครบกำหนดแล้ว
 *
 * ใบใหม่ไม่เข้าคิวนี้เลย เพราะต้องผ่านโหมดเรียนก่อน
 * ปล่อยใบที่ไม่เคยเห็นมาเป็นคำถาม = บังคับให้เดาผิดหนึ่งครั้งก่อนได้เห็นเฉลย
 *
 * เอาที่ค้างนานสุดขึ้นก่อน ไม่ใช่สุ่ม — ใบที่ค้างนานคือใบที่ใกล้ลืมที่สุด
 */
export function buildSrsQueue(
  cards: Card[],
  progress: Map<CardId, Progress>,
  opts: QueueOptions = {},
): Card[] {
  const now = opts.now ?? Date.now()
  const limit = opts.limit ?? Infinity

  const due: Array<{ card: Card; p: Progress }> = []
  for (const card of cards) {
    const p = progress.get(card.id)
    if (p && p.state !== CardState.New && p.due <= now) due.push({ card, p })
  }

  due.sort((a, b) => a.p.due - b.p.due)
  return due.map((d) => d.card).slice(0, limit)
}

/** ใบที่ยังไม่เคยเรียน เรียงตามลำดับที่ deck คำนวณไว้ */
export function buildLessonQueue(
  cards: Card[],
  isNew: (card: Card) => boolean,
  limit = Infinity,
): Card[] {
  return cards
    .filter(isNew)
    .sort((a, b) => Number(a.extra?.['order'] ?? 0) - Number(b.extra?.['order'] ?? 0))
    .slice(0, limit)
}

/**
 * คิวของโหมดอัตโนมัติ — ผสมใบทวนกับใบใหม่ให้เอง
 *
 * ใบที่ครบกำหนดมาก่อนเสมอ เพราะค้างไว้ยิ่งนานยิ่งเสี่ยงลืมของที่ลงแรงไปแล้ว
 * ใบใหม่แทรกทีละใบทุก ๆ NEW_EVERY ใบทวน ไม่ใช่ต่อท้ายทั้งก้อน — เจอของใหม่
 * ประปรายระหว่างทางง่ายกว่าเจอ 15 ใบรวดตอนหมดแรงตอนท้าย
 *
 * ไม่มีใบทวนเลย (วันแรก ๆ) ก็เป็นใบใหม่ล้วน ซึ่งถูกแล้ว
 */
const NEW_EVERY = 4

export function buildAutoQueue(due: Card[], fresh: Card[]): Card[] {
  if (due.length === 0) return fresh
  if (fresh.length === 0) return due

  const out: Card[] = []
  let n = 0
  for (let i = 0; i < due.length; i++) {
    out.push(due[i] as Card)
    if ((i + 1) % NEW_EVERY === 0 && n < fresh.length) out.push(fresh[n++] as Card)
  }
  // ใบใหม่ที่ยังไม่ได้แทรกต่อท้าย — โควตาวันนี้ต้องได้ครบ
  out.push(...fresh.slice(n))
  return out
}

// ---------- drill (kana) ----------

/** ถูกติดกันครบเท่านี้ = พักไว้ก่อน ให้ตัวอื่นได้ออก */
export const DRILL_REST_STREAK = 3

function drillWeight(stat: DrillStat | undefined): number {
  if (!stat || stat.seen === 0) return 10 // ยังไม่เคยเจอ = ต้องได้เจอก่อน
  if (stat.streak >= DRILL_REST_STREAK) return 1 // แน่นแล้ว แต่ยังไม่ตัดขาด
  return Math.max(1, 1 + stat.wrong * 3 - stat.streak)
}

/**
 * สุ่มถ่วงน้ำหนัก: ตัวที่ผิดบ่อยออกบ่อย
 * กันตัวเดิมออกซ้ำติดกัน (ยกเว้นเหลือใบเดียว)
 */
export function pickDrill(
  cards: Card[],
  stats: Map<CardId, DrillStat>,
  previous?: CardId,
): Card | undefined {
  if (cards.length === 0) return undefined
  if (cards.length === 1) return cards[0]

  let total = 0
  const weights = cards.map((c) => {
    const w = c.id === previous ? 0 : drillWeight(stats.get(c.id))
    total += w
    return w
  })

  if (total <= 0) return cards[Math.floor(Math.random() * cards.length)]

  let roll = Math.random() * total
  for (let i = 0; i < cards.length; i++) {
    roll -= weights[i] ?? 0
    if (roll <= 0) return cards[i]
  }
  return cards.at(-1)
}

/**
 * นานกว่านี้ = ไม่ได้นั่งคิดอยู่จริง
 *
 * เวลาที่ไม่ได้อยู่หน้าจอถูกหักไปแล้ว (ดู session.elapsedMs) แต่ยังมีกรณีที่จับไม่ได้
 * — วางมือถือคว่ำไว้ หน้าจอดับแต่แท็บยัง visible · ค่าพวกนี้เข้าค่าเฉลี่ยแล้วดึงยาว
 * จนใบที่จำได้แม่นถูกตีตราว่ายังไม่คล่อง
 */
export const MAX_ANSWER_MS = 60_000

/** ถือว่าจำ kana ตัวนี้ได้แล้ว */
export function drillMastered(stat: DrillStat | undefined): boolean {
  return !!stat && stat.streak >= DRILL_REST_STREAK
}

export function updateDrill(
  cardId: CardId,
  prev: DrillStat | undefined,
  correct: boolean,
  ms: number,
  now = Date.now(),
): DrillStat {
  const base: DrillStat = prev ?? {
    cardId,
    seen: 0,
    wrong: 0,
    streak: 0,
    avgMs: 0,
    lastWrongAt: 0,
    updatedAt: now,
  }

  // ค่าเฉลี่ยแบบถ่วงน้ำหนักครั้งหลัง 30% — ตามฟอร์มปัจจุบัน ไม่ใช่ค่าเฉลี่ยตลอดกาล
  // นับเฉพาะตอนตอบถูก เพราะเวลาตอนตอบผิดคือเวลานั่งงง ไม่ได้บอกความคล่อง
  const usable = correct && ms <= MAX_ANSWER_MS
  const avgMs = usable ? (base.avgMs === 0 ? ms : Math.round(base.avgMs * 0.7 + ms * 0.3)) : base.avgMs

  return {
    cardId,
    seen: base.seen + 1,
    wrong: base.wrong + (correct ? 0 : 1),
    streak: correct ? base.streak + 1 : 0,
    avgMs,
    lastWrongAt: correct ? base.lastWrongAt : now,
    updatedAt: now,
  }
}
